import { showError } from '../../../js/portal/api';
import { getSearchParamsUrl, setSearchParamsUrl } from '../../../js/portal/search_params';
import { fetchVPCs } from '../../../js/portal/main';
import { vpcDropdownQueryFields } from '../../../js/portal/networks';
import { isSearchEnabled } from '../../../js/portal/search_settings';

/**
 * Creates a new selectpicker dropdown including a label and a refresh button.
 *
 * @param {string} label - name label on the left side of the dropdown menu
 * @param {string} id - the id of the selectpicker
 * @param {function} onchange - a function that will be triggered once the selectpicker value changes
 * @param {function} onrefresh - function that is triggered when the refresh button is clicked
 * @param {type[]} selectedValues - a list of VPCs that should be selected if available
 * @param {boolean} deselect - defines if the deselect VPCs button is added to the end of the selectpicker
 * @param {boolean} multiple - defines if you can select one or multiple items in the VPC dropdown
 * @param {boolean} listAll - display all resources that the user has permission to see/edit
 * @param {boolean} disableUrlParams - disable setting and reading URL parameters
 *
 * @returns div including a label, the dropdown and a refresh button
 */
export default function VPCDropdown({
  label,
  id,
  onchange,
  onrefresh,
  selectedValues,
  deselect,
  multiple,
  listAll,
  disableUrlParams,
}) {
  const labelText = label ? label : 'VPC Id';
  const selectId = id ? id : 'vpc-id';
  const headers = { 'Cache-Control': 'max-age=0, must-revalidate' };
  const params = {
    list_all: listAll ? listAll : false,
  };
  const multiSelect = multiple ? true : false;
  const urlParameters = !disableUrlParams ? true : false;

  // selectpicker options: https://developer.snapappointments.com/bootstrap-select/options/
  const select = (
    <select
      required
      class="form-control form-select selectpicker"
      data-live-search="true"
      data-show-subtext="true"
      id={selectId}
      name={selectId}
      data-size="10"
      data-max-options="5"
      data-none-selected-text="- Select one or more VPCs -"
      data-selected-text-format="count > 1"
      data-count-selected-text="{0} VPCs selected"
      data-dropdown-align-right="auto"
      multiple={multiSelect}>
      <option class="defaultDisabled" value="" disabled selected={!multiSelect}>
        - Select a VPC -
      </option>
    </select>
  );

  // Refresh button to reload the VPC data
  const refreshIcon = <i class="fas fa-sync" />;
  const refreshButton = (
    <button
      type="button"
      class="btn btn-light-grey input-group-btn portal-selectpicker-end"
      id="refresh-vpcs"
      title="Reload VPCs">
      {refreshIcon}
    </button>
  );

  // De-select button
  const deselectButton = (
    <button
      type="button"
      class="btn btn-light-grey input-group-btn portal-selectpicker-mid"
      id="deselect-vpcs"
      title="De-select all vpcs">
      <i class="fa fa-user-alt-slash" />
    </button>
  );

  // Represents the whole row including the label, the selectpicker and the refresh button
  const vpcDropdown = (
    <div class="form-group row portal-dropdown">
      {labelText != 'none' && (
        <label for="vpc-id" class="col-form-label">
          {labelText}
        </label>
      )}
      <div class="col input-group">
        {select}
        {deselect ? deselectButton : undefined}
        {refreshButton}
      </div>
    </div>
  );

  // Refresh the selectpicker and add an on change event
  $(select).selectpicker('refresh');
  $(select).on('changed.bs.select', () => {
    const selectedOption = $(select).find(':selected');
    const selectedVPCId = selectedOption.val();
    const selectedId = selectedOption.data('id');
    const selectedUId = selectedOption.data('uuid');

    if (!multiSelect && urlParameters) {
      const previousUrlParam = getSearchParamsUrl('vpc_id');
      if (selectedVPCId && selectedVPCId !== previousUrlParam) {
        setSearchParamsUrl({ vpc_id: selectedVPCId });
      }
    }

    if (onchange && selectedVPCId) {
      onchange('vpc_id', 'network', selectedOption, selectedVPCId, selectedId, selectedUId);
    }
  });

  $(select).on('reload-dropdown-options', event => {
    const dropdownSettings = Object.assign(
      {
        selectedValues,
        multiSelect,
        urlParameters,
      },
      event.detail,
    );

    let additionalParams = {
      list_all: event?.detail?.allResources,
    };

    loadVPCs(
      select,
      refreshIcon,
      dropdownSettings.selectedValues,
      dropdownSettings.multiSelect,
      dropdownSettings.urlParameters,
      headers,
      additionalParams,
    );
  });

  // Reload the VPC records from the REST API (with cache-control headers)
  $(refreshButton).on('click', event => {
    if (onrefresh) {
      onrefresh(event, select);
    } else {
      loadVPCs(select, refreshIcon, selectedValues, multiSelect, urlParameters, headers, params);
    }
  });

  // De-select all VPCs from the selectpicker
  $(deselectButton).on('click', () => {
    $(select).selectpicker('deselectAll');
    $(select).selectpicker('refresh');
  });

  // Load the VPC records from the REST API (without cache-control headers)
  loadVPCs(select, refreshIcon, selectedValues, multiSelect, urlParameters, {}, params);
  return vpcDropdown;
}

/**
 * Populates the selectpicker dropdown with the values from the REST API endpoint. Deletes all active dropdown items
 * before adding the new items.
 *
 * @param {JSX.IntrinsicElements.select} select
 * @param {JSX.Element} refreshIcon
 * @param {type[]} selectedValues
 * @param {boolean} multiSelect
 * @param {boolean} urlParameters
 * @param {object} headers
 * @param {object} params
 */
async function loadVPCs(select, refreshIcon, selectedValues, multiSelect, urlParameters, headers, params) {
  startLoading(select, refreshIcon);

  try {
    const selected_vpc_id = !multiSelect && urlParameters && getSearchParamsUrl('vpc_id');
    const number_vpcs = { private: 0, public: 0, other: 0 };
    await fetchAllVPCsWithRetry(select, headers, params, selected_vpc_id, selectedValues, number_vpcs);

    $(select).trigger('changed.bs.select');
  } catch (err) {
    showError(err);
  } finally {
    finishLoading(select, refreshIcon);
  }
}

/**
 * Sets the dropdown state to loading
 *
 * @param {JSX.IntrinsicElements.select} select
 * @param {JSX.Element} refreshIcon
 */
function startLoading(select, refreshIcon) {
  $(refreshIcon).addClass('fa-spin');
  select.querySelectorAll('option:not(.defaultDisabled)').forEach(el => el.remove());
  $(select).attr('disabled', true).selectpicker('refresh');
}

/**
 * Sets the dropdown state to ready
 *
 * @param {JSX.IntrinsicElements.select} select
 * @param {JSX.Element} refreshIcon
 */
function finishLoading(select, refreshIcon) {
  $(refreshIcon).removeClass('fa-spin');
  $(select).attr('disabled', false);
  $(select).selectpicker('refresh');
}

/**
 * Fetches all VPCs with retry logic
 *
 * @param {JSX.IntrinsicElements.select} select
 * @param {object} headers
 * @param {object} params
 * @param {string} selected_vpc_id
 * @param {array} selectedValues
 * @param {object} number_vpcs
 */
async function fetchAllVPCsWithRetry(select, headers, params, selected_vpc_id, selectedValues, number_vpcs) {
  let vpc_search_enable = isSearchEnabled('vpc_search_enable');
  let maxRetries = 1; // Allow one retry with different search method
  let retryCount = 0;

  while (retryCount <= maxRetries) {
    try {
      await fetchAndProcessAllVPCs(
        select,
        vpc_search_enable,
        headers,
        params,
        selected_vpc_id,
        selectedValues,
        number_vpcs,
      );
      break; // Success, exit the retry loop
    } catch (error) {
      if (vpc_search_enable && retryCount < maxRetries) {
        // Switch to DynamoDB if OpenSearch fails
        console.debug('OpenSearch returned an error, retrying fetch VPCs search with dynamodb');
        vpc_search_enable = false;
        sessionStorage.setItem('vpc_search_enable', 'false');
        retryCount++;
      } else {
        // We've already tried both methods or reached max retries
        throw error;
      }
    }
  }
}

/**
 * Fetches and processes all VPCs, handling pagination
 *
 * @param {JSX.IntrinsicElements.select} select
 * @param {boolean} vpc_search_enable
 * @param {object} headers
 * @param {object} params
 * @param {string} selected_vpc_id
 * @param {array} selectedValues
 * @param {object} number_vpcs
 */
async function fetchAndProcessAllVPCs(
  select,
  vpc_search_enable,
  headers,
  params,
  selected_vpc_id,
  selectedValues,
  number_vpcs,
) {
  const dropdownFields = vpcDropdownQueryFields.join(',');
  let page = 0;
  let options = [];
  let next_token = null;

  // Add fields and size to params
  const requestParams = {
    ...params,
    fields: dropdownFields,
    size: 2000,
  };

  do {
    // Add next_token to params if available
    if (next_token) {
      requestParams.next_token = next_token;
    }

    const response = await fetchVPCs(vpc_search_enable, headers, requestParams);
    const { items, next_token: newToken } = response;

    // Process this batch of VPCs
    options = processVPCItems(items, selected_vpc_id, selectedValues, number_vpcs, options, select);

    // Dispatch event with current results
    dispatchDropdownEvent(options, number_vpcs, params, page, select);

    // Update for next iteration
    next_token = newToken;
    page += 1;
  } while (next_token);
}

/**
 * Processes VPC items and converts them to option elements
 *
 * @param {array} items VPC items from API
 * @param {string} selected_vpc_id Currently selected VPC ID
 * @param {array} selectedValues Array of selected VPC IDs
 * @param {object} number_vpcs Counter object for different VPC types
 * @param {array} options Existing options
 * @param {JSX.IntrinsicElements.select} select The select element
 * @returns {array} Updated options array
 */
function processVPCItems(items, selected_vpc_id, selectedValues, number_vpcs, options, select) {
  const newOptions = items.map(vpc => {
    let opt = document.createElement('option');
    opt.value = vpc.vpc_id;
    opt.dataset.cidr = vpc.cidr;
    opt.dataset.id = vpc.id;
    opt.dataset.uuid = vpc.uuid;
    opt.dataset.content = createOptionContent(vpc);

    // Update counter for VPC types
    countVPCByType(vpc, number_vpcs);

    // Check if this option should be selected
    opt.selected = shouldSelectVPC(vpc.vpc_id, selected_vpc_id, selectedValues);

    return opt;
  });

  // Combine and sort all options
  const allOptions = [...options, ...newOptions];
  allOptions.sort((a, b) => a.value.localeCompare(b.value));

  // Add options to the select element
  newOptions.forEach(option => {
    select.appendChild(option);
  });

  return allOptions;
}

/**
 * Creates the HTML content for a dropdown option
 *
 * @param {object} vpc VPC data
 * @returns {string} HTML content
 */
function createOptionContent(vpc) {
  return (
    `<span class="bs-dropdown-badge">${vpc.vpc_id}</span>` +
    `<span class="bs-dropdown-badge cidr">${vpc.cidr}</span> ` +
    `${vpc.ipv6_cidr ? `<span class="bs-dropdown-badge cidr">${vpc.ipv6_cidr}</span>` : ''}` +
    `<span class="bs-dropdown-item-text">${vpc.name ? vpc.name : '-'}</span>` +
    `<span class="bs-dropdown-badge new">${vpc.aws_account_id}</span>`
  );
}

/**
 * Increments the counter for VPC type
 *
 * @param {object} vpc VPC data
 * @param {object} number_vpcs Counter object
 */
function countVPCByType(vpc, number_vpcs) {
  if (Object.prototype.hasOwnProperty.call(number_vpcs, vpc.network_type)) {
    number_vpcs[vpc.network_type] += 1;
  } else {
    number_vpcs.other += 1;
  }
}

/**
 * Determines if a VPC should be selected
 *
 * @param {string} vpc_id VPC ID to check
 * @param {string} selected_vpc_id Currently selected VPC ID from URL
 * @param {array} selectedValues Array of selected VPC IDs
 * @returns {boolean} True if the VPC should be selected
 */
function shouldSelectVPC(vpc_id, selected_vpc_id, selectedValues) {
  return (selected_vpc_id && selected_vpc_id == vpc_id) || (selectedValues && selectedValues.includes(vpc_id));
}

/**
 * Dispatches a custom event with VPC dropdown loading results
 *
 * @param {array} options Options in the dropdown
 * @param {object} number_vpcs Counter object for VPC types
 * @param {object} params Request parameters
 * @param {number} page Current page number
 * @param {JSX.IntrinsicElements.select} select The select element
 */
function dispatchDropdownEvent(options, number_vpcs, params, page, select) {
  const event = new CustomEvent('dropdown-options-loaded', {
    detail: {
      number_active: options?.length,
      number_private: number_vpcs.private,
      number_public: number_vpcs.public,
      number_other: number_vpcs.other,
      list_all: params.list_all,
      page,
    },
  });
  select.dispatchEvent(event);
}
